import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Loader2, Search } from 'lucide-react';
import {
  placementsApi,
  PlacementBatchSummary,
  PlacementBatchDetail,
  PlacementManageTrainee,
  PostPlacementTracking as PostPlacementTrackingPayload,
} from '../api';

const SLOT_GROUPS: Array<{
  label: string;
  slots: Array<{ slot: string; label: string }>;
}> = [
  {
    label: '1st Month Weekly Call',
    slots: [
      { slot: 'FIRST_MONTH_WEEK_1', label: '1st Week' },
      { slot: 'FIRST_MONTH_WEEK_2', label: '2nd Week' },
      { slot: 'FIRST_MONTH_WEEK_3', label: '3rd Week' },
      { slot: 'FIRST_MONTH_WEEK_4', label: '4th Week' },
    ],
  },
  {
    label: '2nd Month Fortnightly Call',
    slots: [
      { slot: 'SECOND_MONTH_FORTNIGHT_1', label: '1st Fortnight' },
      { slot: 'SECOND_MONTH_FORTNIGHT_2', label: '2nd Fortnight' },
    ],
  },
  {
    label: '3rd Month',
    slots: [{ slot: 'THIRD_MONTH_FINAL', label: 'Final Call' }],
  },
];

const TOTAL_SLOTS = SLOT_GROUPS.reduce(
  (total, group) => total + group.slots.length,
  0
);

const toDateInput = (iso?: string | null) => {
  if (!iso) return '';
  return iso.slice(0, 10);
};

const formatDateDisplay = (iso?: string | null) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  return date.toLocaleDateString('en-IN');
};

const PostPlacementTracking: React.FC = () => {
  const [batchSummaries, setBatchSummaries] = useState<PlacementBatchSummary[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [batchDetail, setBatchDetail] = useState<PlacementBatchDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [trackingInputs, setTrackingInputs] = useState<Record<number, string>>({});
  const [trackingDates, setTrackingDates] = useState<Record<number, string>>({});
  const [trackingSaving, setTrackingSaving] = useState<Record<number, boolean>>({});
  const [trackingSuccess, setTrackingSuccess] = useState<Record<number, string | null>>({});
  const [trackingErrors, setTrackingErrors] = useState<Record<number, string | null>>({});

  const [selectedTraineeId, setSelectedTraineeId] = useState<number | null>(null);
  const [traineeSearch, setTraineeSearch] = useState('');

  const loadBatches = useCallback(async () => {
    setBatchLoading(true);
    setBatchError(null);
    try {
      const response = await placementsApi.listManageBatches();
      const batches = response.data?.data?.batches ?? [];
      setBatchSummaries(
        batches.filter((batch) =>
          ['PostPlacement', 'Completed'].includes(batch.status)
        )
      );
    } catch (err) {
      const parsed =
        (err as any)?.message ??
        'Failed to load batches. Please try again later.';
      setBatchError(parsed);
    } finally {
      setBatchLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const initializeDetailState = useCallback((detail: PlacementBatchDetail) => {
    const nextInputs: Record<number, string> = {};
    const nextDates: Record<number, string> = {};

    detail.trainees.forEach((trainee) => {
      trainee.tracking?.calls.forEach((call) => {
        nextInputs[call.call_id] = call.remark ?? '';
        nextDates[call.call_id] = toDateInput(call.call_made_on);
      });
    });

    setTrackingInputs(nextInputs);
    setTrackingDates(nextDates);
    setTrackingSaving({});
    setTrackingSuccess({});
    setTrackingErrors({});
  }, []);

  const handleSelectBatch = useCallback(
    async (batch: PlacementBatchSummary) => {
      const batchId = batch.batch_id;
      setSelectedBatchId(batchId);
      setDetailLoading(true);
      setDetailError(null);
      try {
        const response = await placementsApi.getBatchDetail(batchId);
        const detail = response.data?.data;
        if (!detail) {
          throw new Error('Unable to load post-placement tracking detail.');
        }
        setBatchDetail(detail);
        initializeDetailState(detail);
        const defaultTrainee =
            detail.trainees.find((trainee) => trainee.tracking) ??
            detail.trainees[0] ??
            null;
        setSelectedTraineeId(defaultTrainee?.trainee_id ?? null);
      } catch (err) {
        const parsed =
          (err as any)?.message ??
          'Failed to load post-placement tracking information.';
        setDetailError(parsed);
        setBatchDetail(null);
        setSelectedTraineeId(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [initializeDetailState]
  );

  const batches = useMemo(
    () =>
      [...batchSummaries].sort(
        (a, b) =>
          new Date(b.updated_at ?? b.created_at).getTime() -
          new Date(a.updated_at ?? a.created_at).getTime()
      ),
    [batchSummaries]
  );

  useEffect(() => {
    if (!selectedBatchId && batches.length) {
      handleSelectBatch(batches[0]);
    }
  }, [batches, selectedBatchId, handleSelectBatch]);

  const trainees = batchDetail?.trainees ?? [];

  const filteredTrainees = useMemo(() => {
    const query = traineeSearch.trim().toLowerCase();
    if (!query) return trainees;
    return trainees.filter((trainee) => {
      const haystack = [
        trainee.name,
        trainee.email,
        trainee.phone,
        trainee.trainee_id?.toString() ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [trainees, traineeSearch]);

  const selectedTrainee = useMemo(
    () => trainees.find((trainee) => trainee.trainee_id === selectedTraineeId) ?? null,
    [trainees, selectedTraineeId]
  );

  const applyTrackingUpdate = useCallback(
    (updatedTracking: PostPlacementTrackingPayload, updatedCallId?: number) => {
      setBatchDetail((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          trainees: prev.trainees.map((trainee) =>
            trainee.tracking && trainee.tracking.tracking_id === updatedTracking.tracking_id
              ? {
                  ...trainee,
                  tracking: {
                    ...updatedTracking,
                  },
                }
              : trainee
          ),
        };
      });

      setTrackingInputs((prev) => {
        const next = { ...prev };
        updatedTracking.calls.forEach((call) => {
          next[call.call_id] = call.remark ?? '';
        });
        return next;
      });

      setTrackingDates((prev) => {
        const next = { ...prev };
        updatedTracking.calls.forEach((call) => {
          next[call.call_id] = toDateInput(call.call_made_on);
        });
        return next;
      });
      if (updatedCallId !== undefined) {
        setTrackingSuccess((prev) => ({
          ...prev,
          [updatedCallId]: new Date().toISOString(),
        }));
        setTrackingErrors((prev) => ({
          ...prev,
          [updatedCallId]: null,
        }));
      }
    },
    []
  );

  const handleRemarkChange = useCallback((callId: number, value: string) => {
    setTrackingInputs((prev) => ({
      ...prev,
      [callId]: value,
    }));
    setTrackingSuccess((prev) => ({ ...prev, [callId]: null }));
    setTrackingErrors((prev) => ({ ...prev, [callId]: null }));
  }, []);

  const handleDateChange = useCallback((callId: number, value: string) => {
    setTrackingDates((prev) => ({
      ...prev,
      [callId]: value,
    }));
    setTrackingSuccess((prev) => ({ ...prev, [callId]: null }));
    setTrackingErrors((prev) => ({ ...prev, [callId]: null }));
  }, []);

  const handleTrackingReset = useCallback(
    (callId: number, originalRemark: string | null, originalDate?: string | null) => {
      setTrackingInputs((prev) => ({ ...prev, [callId]: originalRemark ?? '' }));
      setTrackingDates((prev) => ({ ...prev, [callId]: toDateInput(originalDate) }));
      setTrackingSuccess((prev) => ({ ...prev, [callId]: null }));
      setTrackingErrors((prev) => ({ ...prev, [callId]: null }));
    },
    []
  );

  const handleTrackingSave = useCallback(
    async (callId: number, remark: string, callDate: string | null) => {
      setTrackingSaving((prev) => ({ ...prev, [callId]: true }));
      setTrackingErrors((prev) => ({ ...prev, [callId]: null }));
      try {
        const payload = {
          remark,
          call_made_on: callDate && callDate.trim() ? callDate : null,
        };
        const response = await placementsApi.updateTrackingCall(callId, payload);
        const updatedTracking = response.data?.data?.tracking;
        if (!updatedTracking) {
          throw new Error('Unable to update tracking');
        }
        applyTrackingUpdate(updatedTracking, callId);
      } catch (err) {
        setTrackingErrors((prev) => ({
          ...prev,
          [callId]: (err as any)?.message ?? 'Failed to save tracking information.',
        }));
      } finally {
        setTrackingSaving((prev) => ({ ...prev, [callId]: false }));
      }
    },
    [applyTrackingUpdate]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Post-Placement Tracking</h1>
          <p className="text-sm text-gray-600">
            Track follow-up calls for batches that have moved into post-placement stage.
          </p>
        </div>
        <button
          onClick={() => {
            if (selectedBatchId) {
              placementsApi
                .exportTrackingWorkbook(selectedBatchId)
                .then((response) => {
                  const filename = `PostPlacementTracking_${selectedBatchId}.xlsx`;
                  const blob = new Blob([response.data], {
                    type:
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                  });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = filename;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                })
                .catch(() => {
                  setDetailError('Failed to export tracking workbook.');
                });
            }
          }}
          disabled={!selectedBatchId}
          className={`inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium ${
            selectedBatchId
              ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
          }`}
        >
          <Download className="mr-2 h-4 w-4" />
          Export Workbook
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Batches</h2>
            {batchLoading ? (
              <div className="flex items-center gap-2 text-sm text-primary-600">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading batches...
              </div>
            ) : batchError ? (
              <div className="text-sm text-red-600">{batchError}</div>
            ) : batches.length === 0 ? (
              <div className="text-sm text-gray-500">No batches in post-placement yet.</div>
            ) : (
              <div className="space-y-2">
                {batches.map((batch) => {
                  const isSelected = batch.batch_id === selectedBatchId;
                  const progress =
                    batch.eligible_trainees > 0
                      ? Math.round((batch.placed_trainees / batch.eligible_trainees) * 100)
                      : 0;
                  return (
                    <button
                      key={batch.batch_id}
                      onClick={() => handleSelectBatch(batch)}
                      className={`w-full rounded-md border px-3 py-2 text-left text-sm shadow-sm transition ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 bg-white hover:border-primary-200'
                      }`}
                    >
                      <div className="font-semibold">{batch.batch_code}</div>
                      <div className="text-xs text-gray-500">
                        {batch.center_name ?? '-'} · {batch.program_name ?? '-'}
                      </div>
                      <div className="mt-2">
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>{batch.placed_trainees}/{batch.eligible_trainees} placed</span>
                          <span>{progress}%</span>
                        </div>
                        <div className="mt-1 h-1.5 rounded-full bg-gray-200">
                          <div
                            className="h-1.5 rounded-full bg-primary-500"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {selectedBatchId && (
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Students</h2>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={traineeSearch}
                  onChange={(e) => setTraineeSearch(e.target.value)}
                  placeholder="Search student..."
                  className="w-full rounded-md border px-3 py-2 pl-10 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              {detailLoading ? (
                <div className="flex items-center gap-2 text-sm text-primary-600">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading tracking...
                </div>
              ) : detailError ? (
                <div className="text-sm text-red-600">{detailError}</div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-auto pr-1">
                  {filteredTrainees.map((trainee) => {
                    const tracking = trainee.tracking;
                    const totalSlots = TOTAL_SLOTS;
                    const completedSlots = tracking
                      ? tracking.calls.filter((call) => (call.remark ?? '').trim().length > 0)
                          .length
                      : 0;
                    const progress = totalSlots > 0 ? Math.round((completedSlots / totalSlots) * 100) : 0;
                    const isSelected = trainee.trainee_id === selectedTraineeId;
                    return (
                      <button
                        key={trainee.trainee_id}
                        onClick={() => setSelectedTraineeId(trainee.trainee_id)}
                        className={`w-full rounded-md border px-3 py-2 text-left text-sm transition ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 bg-white hover:border-primary-200'
                        }`}
                      >
                        <div className="font-semibold">{trainee.name}</div>
                        <div className="text-xs text-gray-500">
                          {completedSlots}/{totalSlots} calls completed
                        </div>
                        <div className="mt-1 h-1 rounded-full bg-gray-200">
                          <div
                            className={`h-1 rounded-full ${
                              progress === 100 ? 'bg-green-500' : 'bg-primary-500'
                            }`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </button>
                    );
                  })}
                  {filteredTrainees.length === 0 && (
                    <div className="text-sm text-gray-500">
                      No students match the current filters.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {detailLoading ? (
            <div className="p-8 text-center text-sm text-primary-600">
              <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
              Loading tracking detail...
            </div>
          ) : !selectedTrainee ? (
            <div className="p-8 text-center text-sm text-gray-500">
              Select a student to view post-placement tracking entries.
            </div>
          ) : (
            <>
              <div className="border-b border-gray-200 p-4">
                <div className="flex flex-col gap-1">
                  <div className="text-sm font-semibold text-gray-900">{selectedTrainee.name}</div>
                  <div className="text-xs text-gray-500">
                    {selectedTrainee.email || '-'} · {selectedTrainee.phone || '-'}
                  </div>
                  <div className="text-xs text-gray-500">
                    Placement Status:{' '}
                    {selectedTrainee.has_placement ? 'Placed' : 'Pending'}
                  </div>
                </div>
              </div>

              <div className="p-4 overflow-x-auto">
                <table className="min-w-full rounded-md border border-gray-200 text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Call Category</th>
                      <th className="px-3 py-2 text-left">Slot</th>
                      <th className="px-3 py-2 text-left">Call Made On</th>
                      <th className="px-3 py-2 text-left">Remarks</th>
                      <th className="px-3 py-2 text-left">Updated Info</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {SLOT_GROUPS.map((group) => {
                      const groupSlots = group.slots;
                      return groupSlots.map((entry, index) => {
                        const call = selectedTrainee.tracking?.calls.find(
                          (item) => item.slot === entry.slot
                        );
                        const callId = call?.call_id;
                        const remarkValue =
                          callId !== undefined
                            ? trackingInputs[callId] ?? call?.remark ?? ''
                            : '';
                        const dateValue =
                          callId !== undefined
                            ? trackingDates[callId] ?? toDateInput(call?.call_made_on) ?? ''
                            : '';
                        const saving = callId !== undefined ? trackingSaving[callId] ?? false : false;
                        const error = callId !== undefined ? trackingErrors[callId] ?? null : null;
                        const success = callId !== undefined ? trackingSuccess[callId] ?? null : null;
                        const updatedBy =
                          call?.updated_by?.full_name ??
                          call?.updated_by?.user_id ??
                          '';
                        const updatedAt = call?.updated_at ?? call?.created_at ?? null;
                        const originalRemark = call?.remark ?? '';
                        const originalDate = call?.call_made_on ?? null;
                        const hasChanges =
                          remarkValue !== (originalRemark ?? '') ||
                          dateValue !== toDateInput(originalDate);
                        const canEdit =
                          Boolean(batchDetail?.batch.can_edit_tracking) &&
                          (selectedTrainee.tracking_editable ?? false);

                        return (
                          <tr key={entry.slot} className="align-top">
                            {index === 0 && (
                              <td
                                className="px-3 py-2 text-sm font-medium text-gray-700"
                                rowSpan={groupSlots.length}
                              >
                                {group.label}
                              </td>
                            )}
                            <td className="px-3 py-2 text-sm text-gray-700">{entry.label}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">
                              {callId === undefined ? (
                                <span className="text-xs text-gray-400">Not generated</span>
                              ) : (
                                <input
                                  type="date"
                                  value={dateValue}
                                  onChange={(e) => handleDateChange(callId, e.target.value)}
                                  disabled={!canEdit || saving}
                                  className={`w-full rounded-md border px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                                    !canEdit ? 'bg-gray-100 text-gray-500' : ''
                                  }`}
                                />
                              )}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-700">
                              {callId === undefined ? (
                                <span className="text-xs text-gray-400">Not generated</span>
                              ) : (
                                <textarea
                                  value={remarkValue}
                                  onChange={(e) => handleRemarkChange(callId, e.target.value)}
                                  disabled={!canEdit || saving}
                                  rows={2}
                                  className={`w-full rounded-md border px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                                    !canEdit ? 'bg-gray-100 text-gray-500' : ''
                                  }`}
                                  maxLength={1000}
                                  placeholder="Enter call remarks"
                                />
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500">
                              <div>Updated By: {updatedBy || '�'}</div>
                              <div>
                                Updated At:{' '}
                                {updatedAt ? formatDateDisplay(updatedAt) : '�'}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right text-sm text-gray-700">
                              {callId === undefined ? (
                                <span className="text-xs text-gray-400">—</span>
                              ) : (
                                <div className="flex flex-col items-end gap-2">
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() =>
                                        handleTrackingReset(
                                          callId,
                                          originalRemark,
                                          originalDate
                                        )
                                      }
                                      disabled={!canEdit || saving || !hasChanges}
                                      className={`inline-flex items-center rounded-md border px-3 py-1 text-xs ${
                                        !canEdit || saving || !hasChanges
                                          ? 'cursor-not-allowed border-gray-200 text-gray-300'
                                          : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                                      }`}
                                    >
                                      Reset
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleTrackingSave(
                                          callId,
                                          remarkValue,
                                          dateValue || null
                                        )
                                      }
                                      disabled={!canEdit || !hasChanges || saving}
                                      className={`inline-flex items-center rounded-md px-3 py-1 text-xs font-medium text-white ${
                                        !canEdit || !hasChanges || saving
                                          ? 'cursor-not-allowed bg-primary-300'
                                          : 'bg-primary-600 hover:bg-primary-700'
                                      }`}
                                    >
                                      {saving ? (
                                        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                                      ) : null}
                                      Save
                                    </button>
                                  </div>
                                  {error && (
                                    <div className="text-xs text-red-600">{error}</div>
                                  )}
                                  {success && !error && (
                                    <div className="text-xs text-green-600">
                                      Saved just now
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostPlacementTracking;



